/*
 * A copy of the driver application's module of the same name, kept byte-for-byte except for the
 * storage keys and the prose that named a courier.
 *
 * Nothing in it is design content: it is the OpenID Connect authorization-code flow with PKCE, the
 * token refresh, the expiry and the claim reading, and a rider's phone needs all of it exactly as a
 * courier's does. It is a copy rather than a shared package for the reason
 * `docs/adr/0021-two-consoles-and-no-shared-shell.md` recorded when the second console arrived: a
 * package in `packages/` could not be built by `npm run check` before its consumers, and that would
 * be worse than this duplication. This is the **fourth** copy, which is worth saying out loud: that
 * record's condition for extracting the protocol into a package was "a third console", and the
 * channels have now passed it twice. The extraction is recorded in `docs/OPEN_WORK.md` rather than
 * taken here, because it belongs to `packages/` and this agent owns none of it.
 *
 * `docs/adr/0022-keycloaks-own-page-is-the-sign-in-page.md` records the decision `arrival.tsx`
 * takes, including the two unauthenticated arrivals that must *not* redirect.
 */

/**
 * OpenID Connect, authorization code flow with PKCE, written out rather than imported.
 *
 * The shape is not a choice. This console is a browser application, it cannot hold a client secret,
 * and a public client with PKCE is therefore the only correct option — RFC 7636, and the OAuth 2.0
 * for Browser-Based Apps BCP, both say so in those words.
 *
 * Written here rather than taken from a library for one reason: every dependency in a browser bundle
 * that touches tokens is a supply-chain question, and the protocol is small. What follows is four
 * requests and two pieces of arithmetic — discovery, an authorization URL, a code exchange, a
 * refresh, a random string and a SHA-256 — in about the space the library's own README would take.
 * Nothing here is clever, and that is the point: it can be read end to end before it is trusted.
 *
 * What this module deliberately does **not** do is verify anything. It does not check the access
 * token's signature, and it does not validate the id token at all. A browser cannot hold the
 * verification key, and a client that "validated" a token would be performing a check whose failure
 * it would also be responsible for reporting. The services verify properly —
 * `packages/service-runtime/src/authentication.ts` against a remote JWKS — and this reads claims
 * only so the console can draw the right chrome. The id token is kept for exactly one purpose, as
 * the `id_token_hint` on logout, which is why no nonce is sent: a nonce binds an id token to a
 * session, and an unvalidated hint has nothing to bind.
 *
 * Every dependency is a parameter. `fetch` and `crypto` are passed in rather than reached for, so
 * the tests exercise the real arithmetic against a recorded provider instead of exercising a mock of
 * this file.
 */

/** What a provider's discovery document tells us, reduced to the parts this flow uses. */
export interface ProviderMetadata {
  readonly issuer: string;
  readonly authorizationEndpoint: string;
  readonly tokenEndpoint: string;
  /** Absent on a provider that does not support RP-initiated logout. */
  readonly endSessionEndpoint?: string;
}

/** A set of tokens, with expiry already resolved to a wall-clock instant. */
export interface TokenSet {
  readonly accessToken: string;
  readonly refreshToken?: string;
  readonly idToken?: string;
  /** Epoch milliseconds. Computed from `expires_in` at the moment the response arrived. */
  readonly expiresAt: number;
}

/**
 * A refusal from the provider, carrying the provider's own words.
 *
 * `code` is the OAuth 2.0 `error` field where there was one — `invalid_grant`, `access_denied` — and
 * a name of ours where the failure happened outside that envelope. Branching on it is how the
 * sign-in screen tells "you cancelled" from "your refresh token has expired", which are the same
 * HTTP status and completely different screens.
 */
export class OidcError extends Error {
  public constructor(
    public readonly code: string,
    message: string,
    public override readonly cause?: unknown,
  ) {
    super(message);
    this.name = "OidcError";
  }
}

/** Base64url, no padding: what RFC 7636 asks for and what `btoa` does not give. */
function base64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replaceAll("=", "");
}

/**
 * The PKCE code verifier: 43 characters of base64url over 32 random bytes.
 *
 * RFC 7636 section 4.1 allows 43 to 128 characters and requires a cryptographically random source.
 * 32 bytes is the shortest length that is also the full output of the hash it will be compared
 * through, so there is nothing to gain from more.
 */
export function createCodeVerifier(crypto: Pick<Crypto, "getRandomValues">): string {
  return base64Url(crypto.getRandomValues(new Uint8Array(32)));
}

/**
 * The opaque value that ties a callback to the request that started it.
 *
 * A separate function from the verifier because the two are not interchangeable: the state is sent
 * to the provider and comes back, the verifier never leaves this browser. Generating them from one
 * call would be one edit away from sending the verifier.
 */
export function createState(crypto: Pick<Crypto, "getRandomValues">): string {
  return base64Url(crypto.getRandomValues(new Uint8Array(16)));
}

/** S256, which is the only challenge method this console offers. `plain` exists and is not used. */
export async function createCodeChallenge(
  verifier: string,
  crypto: Pick<Crypto, "subtle">,
): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64Url(new Uint8Array(digest));
}

/**
 * Read a JWT's claims without verifying it.
 *
 * Named for what it does. `decode` would read as the counterpart of a verify that does not exist
 * here, and the whole risk in this file is somebody later believing this checked something.
 */
export function readClaimsWithoutVerifying(token: string): Record<string, unknown> {
  const payload = token.split(".")[1];
  if (payload === undefined) throw new OidcError("malformed_token", "The token is not a JWT");
  const padded = payload.replaceAll("-", "+").replaceAll("_", "/");
  try {
    const binary = atob(padded.padEnd(Math.ceil(padded.length / 4) * 4, "="));
    const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
    const claims = JSON.parse(new TextDecoder().decode(bytes)) as unknown;
    if (claims === null || typeof claims !== "object" || Array.isArray(claims)) {
      throw new OidcError("malformed_token", "The token's claims are not an object");
    }
    return claims as Record<string, unknown>;
  } catch (cause) {
    if (cause instanceof OidcError) throw cause;
    throw new OidcError("malformed_token", "The token's claims could not be read", cause);
  }
}

/**
 * Whether a token is for this platform.
 *
 * `aud` is a string or an array of strings — RFC 7519 section 4.1.3 allows both — and an issuer that
 * emits the single-valued form is not misconfigured. Reading only the array would be the same
 * mistake the service runtime made with `scope` and was bitten by in commit 974737d.
 */
export function audienceIncludes(claims: Record<string, unknown>, audience: string): boolean {
  const value = claims.aud;
  if (typeof value === "string") return value === audience;
  if (Array.isArray(value)) return value.includes(audience);
  return false;
}

interface DiscoveryDocument {
  readonly issuer?: unknown;
  readonly authorization_endpoint?: unknown;
  readonly token_endpoint?: unknown;
  readonly end_session_endpoint?: unknown;
}

/**
 * Fetch the provider's metadata, and refuse it if it names a different issuer.
 *
 * The issuer check is not ceremony. The configured issuer is what the services verify `iss` against,
 * so a discovery document naming a different one means the console would obtain tokens that every
 * service refuses — 142 of them, all with `invalid_access_token`, and nothing on any screen saying
 * why. Failing here names the actual problem once.
 */
export async function discover(
  issuer: string,
  call: typeof globalThis.fetch,
): Promise<ProviderMetadata> {
  const url = `${issuer.replace(/\/$/, "")}/.well-known/openid-configuration`;
  let response: Response;
  try {
    response = await call(url, { headers: { accept: "application/json" } });
  } catch (cause) {
    throw new OidcError("provider_unreachable", "The identity provider did not answer", cause);
  }
  if (!response.ok) {
    throw new OidcError(
      "provider_unreachable",
      `The identity provider answered ${String(response.status)} for its own metadata`,
    );
  }

  const document = (await response.json()) as DiscoveryDocument;
  if (
    typeof document.issuer !== "string" ||
    typeof document.authorization_endpoint !== "string" ||
    typeof document.token_endpoint !== "string"
  ) {
    throw new OidcError(
      "provider_metadata_incomplete",
      "The identity provider's metadata has no issuer, authorization endpoint or token endpoint",
    );
  }
  if (document.issuer.replace(/\/$/, "") !== issuer.replace(/\/$/, "")) {
    throw new OidcError(
      "provider_issuer_mismatch",
      `The identity provider calls itself ${document.issuer}, and the services verify ${issuer}`,
    );
  }

  return {
    issuer: document.issuer,
    authorizationEndpoint: document.authorization_endpoint,
    tokenEndpoint: document.token_endpoint,
    ...(typeof document.end_session_endpoint === "string"
      ? { endSessionEndpoint: document.end_session_endpoint }
      : {}),
  };
}

export interface AuthorizationRequest {
  readonly metadata: ProviderMetadata;
  readonly clientId: string;
  readonly redirectUri: string;
  readonly scopes: string;
  readonly state: string;
  readonly codeChallenge: string;
  /**
   * The email the operator typed, passed through as the standard `login_hint`.
   *
   * OpenID Connect Core section 3.1.2.1. It is a hint and nothing more: the provider decides what to
   * do with it, and the console never sees whether it was used.
   */
  readonly loginHint?: string;
  /** Requested where the provider honours resource indicators; see `configuration.ts`. */
  readonly audience?: string;
}

export function authorizationUrl(request: AuthorizationRequest): string {
  const url = new URL(request.metadata.authorizationEndpoint);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("client_id", request.clientId);
  url.searchParams.set("redirect_uri", request.redirectUri);
  url.searchParams.set("scope", request.scopes);
  url.searchParams.set("state", request.state);
  url.searchParams.set("code_challenge", request.codeChallenge);
  url.searchParams.set("code_challenge_method", "S256");
  if (request.loginHint !== undefined && request.loginHint.length > 0) {
    url.searchParams.set("login_hint", request.loginHint);
  }
  if (request.audience !== undefined && request.audience.length > 0) {
    url.searchParams.set("audience", request.audience);
  }
  return url.toString();
}

interface TokenResponse {
  readonly access_token?: unknown;
  readonly refresh_token?: unknown;
  readonly id_token?: unknown;
  readonly expires_in?: unknown;
  readonly error?: unknown;
  readonly error_description?: unknown;
}

/**
 * Post to the token endpoint and read the answer, including the failure envelope.
 *
 * No client authentication, because a public client has none to offer. RFC 6749 section 2.3 and the
 * PKCE extension together are what make that safe: the authorization code is useless without the
 * verifier, and the verifier never left this browser.
 */
async function postToTokenEndpoint(
  metadata: ProviderMetadata,
  form: URLSearchParams,
  call: typeof globalThis.fetch,
  now: number,
): Promise<TokenSet> {
  let response: Response;
  try {
    response = await call(metadata.tokenEndpoint, {
      method: "POST",
      headers: {
        "content-type": "application/x-www-form-urlencoded",
        accept: "application/json",
      },
      body: form.toString(),
    });
  } catch (cause) {
    throw new OidcError("provider_unreachable", "The identity provider did not answer", cause);
  }

  let payload: TokenResponse;
  try {
    payload = (await response.json()) as TokenResponse;
  } catch (cause) {
    throw new OidcError(
      "provider_response_unreadable",
      `The identity provider answered ${String(response.status)} without a JSON body`,
      cause,
    );
  }

  if (!response.ok || typeof payload.error === "string") {
    // The provider's own error name, not the status. `invalid_grant` on a refresh means the session
    // is over; `invalid_grant` on an exchange means the code was replayed or the verifier did not
    // match. A status alone cannot separate those and the two lead to different screens.
    throw new OidcError(
      typeof payload.error === "string" ? payload.error : `http_${String(response.status)}`,
      typeof payload.error_description === "string"
        ? payload.error_description
        : "The identity provider refused the request",
    );
  }

  if (typeof payload.access_token !== "string") {
    throw new OidcError(
      "provider_response_unreadable",
      "The identity provider's answer carried no access token",
    );
  }

  /*
   * A missing `expires_in` is treated as already expired rather than as forever.
   *
   * RFC 6749 section 5.1 makes the field optional, and the only safe reading of its absence in a
   * browser is that the console does not know — which means refreshing before the next request
   * rather than holding a token of unknown age until a service refuses it.
   */
  const lifetimeSeconds = typeof payload.expires_in === "number" ? payload.expires_in : 0;

  return {
    accessToken: payload.access_token,
    expiresAt: now + lifetimeSeconds * 1000,
    ...(typeof payload.refresh_token === "string" ? { refreshToken: payload.refresh_token } : {}),
    ...(typeof payload.id_token === "string" ? { idToken: payload.id_token } : {}),
  };
}

export async function exchangeCode(
  input: {
    readonly metadata: ProviderMetadata;
    readonly clientId: string;
    readonly redirectUri: string;
    readonly code: string;
    readonly codeVerifier: string;
  },
  call: typeof globalThis.fetch,
  now: number,
): Promise<TokenSet> {
  const form = new URLSearchParams({
    grant_type: "authorization_code",
    code: input.code,
    redirect_uri: input.redirectUri,
    client_id: input.clientId,
    code_verifier: input.codeVerifier,
  });
  return postToTokenEndpoint(input.metadata, form, call, now);
}

export async function refreshTokens(
  input: {
    readonly metadata: ProviderMetadata;
    readonly clientId: string;
    readonly refreshToken: string;
  },
  call: typeof globalThis.fetch,
  now: number,
): Promise<TokenSet> {
  const form = new URLSearchParams({
    grant_type: "refresh_token",
    refresh_token: input.refreshToken,
    client_id: input.clientId,
  });
  const refreshed = await postToTokenEndpoint(input.metadata, form, call, now);
  /*
   * Keep the previous refresh token when the provider does not send a new one.
   *
   * Rotation is the norm and Keycloak does rotate, but RFC 6749 section 6 leaves the field optional
   * — and a console that dropped the old token on a provider that reuses them would have exactly one
   * refresh in it, after which every operator would be signed out at the first expiry.
   */
  return refreshed.refreshToken === undefined
    ? { ...refreshed, refreshToken: input.refreshToken }
    : refreshed;
}

/**
 * Where to send the browser to end the provider's session as well as this one.
 *
 * Clearing the tokens here would leave the operator signed in at the provider, so the next sign-in
 * would complete without asking for anything — which is not what "sign out" means to the person who
 * clicked it, and is actively wrong on a shared dispatch workstation.
 */
export function endSessionUrl(input: {
  readonly metadata: ProviderMetadata;
  readonly clientId: string;
  readonly postLogoutRedirectUri: string;
  readonly idToken?: string;
}): string | undefined {
  if (input.metadata.endSessionEndpoint === undefined) return undefined;
  const url = new URL(input.metadata.endSessionEndpoint);
  url.searchParams.set("client_id", input.clientId);
  url.searchParams.set("post_logout_redirect_uri", input.postLogoutRedirectUri);
  if (input.idToken !== undefined) url.searchParams.set("id_token_hint", input.idToken);
  return url.toString();
}
